import { preload } from 'react-dom';

import {
  Banner as BannerRoot,
  BannerBody,
  BannerContent,
  BannerCta,
  BannerDescription,
  BannerEyebrow,
  BannerImage,
  BannerTitle,
  BannerVariant,
} from '@vs-shop/commerce-ui/banner';

import { getBannerMediaProps } from '../../../../utils/getBannerMediaProps';
import { getMemoizedIntlShapeWithDefaultLocale } from '../../../../utils/intl';

import type { HomepageBanner } from '../../../../effect/schema/openapi/backend';

type Props = {
  banner: HomepageBanner;
};

/**
 * One slide of the homepage band: the shared `@vs-shop/commerce-ui` banner in
 * its `hero` appearance, fed this tenant's CMS row.
 *
 * What stays here is what cannot live in a shared package — the CMS row shape,
 * the Sylius/Liip media pipeline behind `getBannerMediaProps`, the per-instance
 * CMS colours, which reach the parts as final CSS values on the native `style`
 * prop, and the translated default button label.
 *
 * The call to action is gated on the DESTINATION rather than on its copy,
 * unlike the section's other rcprofi call site: this one supplies a default
 * label, so a row that carries no button text still has something to draw, and
 * "is there anywhere to go" is the only question left that can decide whether a
 * control belongs on the band.
 *
 * The image carries a preload hint because this band is the storefront's
 * largest contentful paint. `BannerImage` renders the media itself; the hint
 * is about WHEN the browser starts fetching, which is a page-level concern and
 * so belongs to the page that leads with it.
 */
export default function HomepageBannerCarouselItem({ banner }: Props) {
  const intl = getMemoizedIntlShapeWithDefaultLocale();

  const media = getBannerMediaProps(banner, BannerVariant.HERO);
  const title = banner.title ?? null;
  const link = banner.link ?? null;

  if (media.image.primary !== null) {
    preload(media.image.primary, { as: 'image' });
  }

  return (
    <BannerRoot variant={BannerVariant.HERO}>
      <BannerImage {...media} alt={title ?? ''} />

      <BannerContent variant={BannerVariant.HERO}>
        <BannerBody
          variant={BannerVariant.HERO}
          {...(banner.highlightEnabled && banner.highlightColor
            ? { highlightColor: banner.highlightColor }
            : {})}
        >
          {banner.label ? (
            <BannerEyebrow variant={BannerVariant.HERO} style={{ color: banner.labelColor }}>
              {banner.label}
            </BannerEyebrow>
          ) : null}

          {title ? (
            <BannerTitle variant={BannerVariant.HERO} style={{ color: banner.titleColor }}>
              {title}
            </BannerTitle>
          ) : null}

          {banner.description ? (
            <BannerDescription
              variant={BannerVariant.HERO}
              style={{ color: banner.descriptionColor }}
            >
              {banner.description}
            </BannerDescription>
          ) : null}
        </BannerBody>

        {link === null ? null : (
          <BannerCta
            variant={BannerVariant.HERO}
            href={link}
            style={{ color: banner.buttonTextColor, borderColor: banner.buttonTextColor }}
          >
            {banner.buttonText ??
              intl.formatMessage({
                id: 'homepage.banner.link',
                defaultMessage: 'ZJISTIT VÍCE',
              })}
          </BannerCta>
        )}
      </BannerContent>
    </BannerRoot>
  );
}
